// ✅ Static metadata export (used in project listing page)
export const metadata = {
  title: 'Modern Master Bedroom Remodeling in Bel Air, Los Angeles CA 90077',
  description:
    'A modern master bedroom remodeling project in Bel Air, Los Angeles featuring custom lighting, refined finishes, clean architectural lines, and luxury comfort by Modern Build.',
  link: '/services/home-remodeling',
  height: 'h-[467px]',
  image:
    '/projects/modern-master-bedroom-remodeling-in-bel-air-los-angeles-90077/modern-master-bedroom-remodeling-in-bel-air-los-angeles-90077.webp',
};

import fs from 'fs';
import path from 'path';
import Head from 'next/head';
import Header from '../../../components/Header';
import Hero from '../../../components/Hero';
import ContentBlock from '../../../components/ContentBlock';
import { VideoSection } from '../../../components/Videos';
import Gallery from '../../../components/Gallery';
import SocialProof from '../../../components/SocialProof';
import ContactUs from '../../../components/ContactUs';
import Footer from '../../../components/Footer';
import { generateWebPageSchema } from '../../../utils/generateWebPageSchema';

export async function getStaticProps() {
  const slug =
    'modern-master-bedroom-remodeling-in-bel-air-los-angeles-90077';

  const galleryPath = path.join(
    process.cwd(),
    'public',
    'projects',
    slug,
    'gallery'
  );

  let galleryImages = [];

  // ✅ Load gallery images
  if (fs.existsSync(galleryPath)) {
    const files = fs.readdirSync(galleryPath).filter(file =>
      /\.(jpg|jpeg|png|webp)$/i.test(file)
    );

    galleryImages = files.map(file => ({
      src: `/projects/${slug}/gallery/${file}`,
      alt: file
        .replace(/\.(jpg|jpeg|png|webp)$/i, '')
        .replace(/[-_]/g, ' '),
      title: file
        .replace(/\.(jpg|jpeg|png|webp)$/i, '')
        .replace(/[-_]/g, ' '),
    }));
  }

  return {
    props: {
      galleryImages,
    },
  };
}

export default function ModernMasterBedroomBelAir({
  galleryImages,
}) {
  const meta = metadata;

  return (
    <>
      <Head>
        <title>{meta.title}</title>
        <meta name="title" content={meta.title} />
        <meta name="description" content={meta.description} />
        <meta name="author" content="Modern Build Home Remodeling" />
        <meta
          name="keywords"
          content="master bedroom remodeling Bel Air, luxury bedroom remodel 90077, modern bedroom renovation Los Angeles, Modern Build"
        />

        <link
          rel="canonical"
          href="https://modernbuildremodel.com/projects/modern-master-bedroom-remodeling-in-bel-air-los-angeles-90077"
        />

        {/* Open Graph */}
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta
          property="og:image"
          content={`https://modernbuildremodel.com${meta.image}`}
        />
        <meta
          property="og:url"
          content="https://modernbuildremodel.com/projects/modern-master-bedroom-remodeling-in-bel-air-los-angeles-90077"
        />
        <meta property="og:type" content="article" />
        <meta
          property="og:site_name"
          content="Modern Build Home Remodeling"
        />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={meta.title}
        />
        <meta
          name="twitter:description"
          content={meta.description}
        />
        <meta
          name="twitter:image"
          content={`https://modernbuildremodel.com${meta.image}`}
        />

        {/* Breadcrumb Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: 'Home',
                  item: 'https://modernbuildremodel.com',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: 'Projects',
                  item: 'https://modernbuildremodel.com/projects',
                },
                {
                  '@type': 'ListItem',
                  position: 3,
                  name:
                    'Modern Master Bedroom Remodeling in Bel Air CA 90077',
                  item:
                    'https://modernbuildremodel.com/projects/modern-master-bedroom-remodeling-in-bel-air-los-angeles-90077',
                },
              ],
            }),
          }}
        />

        {/* WebPage Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              generateWebPageSchema({
                title:
                  'Modern Master Bedroom Remodeling in Bel Air, Los Angeles CA 90077 | Modern Build',
                description:
                  'Explore a luxury master bedroom remodel in Bel Air featuring modern finishes, custom lighting, and refined architectural details.',
                slug:
                  '/projects/modern-master-bedroom-remodeling-in-bel-air-los-angeles-90077',
              })
            ),
          }}
        />
      </Head>

      <Header />

      <Hero
        category="modernMasterBedroomBelAir90077"
        page="page"
        hasContentH1={false}
      />

      <ContentBlock
        subtitle="PROJECT OVERVIEW"
        title="Modern Master Bedroom Remodeling in Bel Air"
        description="This master bedroom remodeling project in Bel Air, Los Angeles transformed a dated private suite into a modern, calming retreat. The renovation focused on comfort, lighting, and architectural balance."
        extraText="Every detail was designed to enhance relaxation, privacy, and long term livability while maintaining a refined, luxury feel."
        buttonText="Plan My Bedroom Remodel"
        buttonLink="/services/complete-home-remodeling"
      />

      <Gallery images={galleryImages} />

      <section className="bg-white py-[120px]">
        <div className="container mx-auto px-6">
          <VideoSection
            videoUrl="https://www.youtube.com/embed/Yqq53bQVY5w"
            serviceType="Bedroom Remodeling"
            location="Bel Air, Los Angeles"
            description="Watch how this Bel Air master bedroom was transformed into a modern, peaceful retreat with custom finishes and luxury details."
            serviceLink="/services/complete-home-remodeling"
            coverImage={meta.image}
            isVideoLeft={true}
          />
        </div>
      </section>

<section className="bg-white py-[120px]">
  <div className="container mx-auto px-6 max-w-4xl">

    <h2 className="text-2xl font-light text-primary leading-tight font-gilda mb-4">
      Modern Master Bedroom Remodeling in Bel Air, CA 90077
    </h2>

    <p className="text-gray font-light text-lg leading-relaxed">
      This modern master bedroom remodel in Bel Air, CA 90077 demonstrates how thoughtful design can elevate everyday living. The homeowners wanted a private space that felt calm, luxurious, and highly functional. Explore <a
        href="https://modernbuildremodel.com/complete-home-remodeling/california/bel-air/90077"
        className="text-primary underline"
      >
        master bedroom remodeling in Bel Air (ZIP 90077)
      </a> to see our work.
    </p>

    <p className="text-gray font-light text-lg leading-relaxed">
      The project focused on creating a balanced environment supporting rest, organization, and comfort. You can also review <a
        href="https://modernbuildremodel.com/complete-home-remodeling/california/bel-air/90077"
        className="text-primary underline"
      >
        Bel Air 90077 master bedroom remodel
      </a> examples for more inspiration.
    </p>

    <p className="text-gray font-light text-lg leading-relaxed">
      For other high-end bedroom or home remodeling projects across Los Angeles, see our <a
        href="https://modernbuildremodel.com/services/complete-home-remodeling"
        className="text-primary underline"
      >
        main Home Remodeling services
      </a>.
    </p>

    <h3 className="text-xl font-light text-black leading-tight font-gilda mt-10 mb-4">
      Project Overview
    </h3>

    <p className="text-gray font-light text-lg leading-relaxed">
      The original bedroom lacked cohesive design, proper lighting, and efficient storage. Through a Bel Air 90077 master bedroom remodel, we created a space that perfectly combines aesthetics and functionality.
    </p>

    <p className="text-gray font-light text-lg leading-relaxed">
      Our objective was to create a unified space that blends visual appeal with everyday practicality.
    </p>

    <h3 className="text-xl font-light text-black leading-tight font-gilda mt-10 mb-4">
      Design Strategy
    </h3>

    <p className="text-gray font-light text-lg leading-relaxed">
      Layered lighting, neutral tones, clean lines, and premium materials were selected to create a timeless, functional, and luxurious space.
    </p>

    <h3 className="text-xl font-light text-black leading-tight font-gilda mt-10 mb-4">
      Craftsmanship and Execution
    </h3>

    <p className="text-gray font-light text-lg leading-relaxed">
      Every phase, from framing to finish installation, was handled with precision to ensure flawless execution.
    </p>

    <h3 className="text-xl font-light text-black leading-tight font-gilda mt-10 mb-4">
      Results and Homeowner Experience
    </h3>

    <p className="text-gray font-light text-lg leading-relaxed">
      The new master bedroom feels spacious, peaceful, and highly personalized. Improved lighting, finishes, and layout flow have transformed daily use.
    </p>

  </div>
</section>



      <SocialProof />
      <ContactUs />
      <Footer />
    </>
  );
}
